import { IBaseComponent } from '@codesuma/baseline'
import { ListItem } from '../../components/list-item'
import { haptic } from '../../utils/haptics'

type Row = ReturnType<typeof ListItem>

interface ReorderDeps {
    listEl: IBaseComponent
    getRows: () => Row[]
    setRows: (rows: Row[]) => void
    onReorder: (rows: Row[]) => void
}

// How long a still finger has to rest on a row before it lifts and can be
// moved. Short enough that the old "hold briefly, then move" muscle memory
// still works; long enough that an ordinary tap never lifts anything.
const LIFT_DELAY = 300

// Auto-scroll while dragging: hold the row within this many pixels of the top or
// bottom of the list and it keeps scrolling, so a row can travel further than one
// screen. Speed ramps up the closer to the edge the finger is.
const EDGE = 72
const MAX_SPEED = 16

// Long-press a row: it lifts to say it's ready, then drag to reorder. The
// container drives the per-item transforms; on drop the new order is handed
// back via onReorder. Exposes isDragging() so the page can skip re-rendering
// mid-drag.
export const createReorder = ({ listEl, getRows, setRows, onReorder }: ReorderDeps) => {
    let dragging: Row | null = null
    let armed: Row | null = null // lifted and ready; the grab starts on first move
    let pressTimer: ReturnType<typeof setTimeout> | undefined
    let startY = 0
    let pointerY = 0     // last seen finger position — the auto-scroll frame re-reads it
    let startScroll = 0  // list scroll when the grab began, so scrolling counts as movement
    let dy = 0
    let startIndex = 0
    let rowH = 56
    let frame = 0

    const findRow = (target: EventTarget | null) =>
        getRows().find(r => r.el === target || r.el.contains(target as Node))

    const targetIndex = () =>
        Math.max(0, Math.min(getRows().length - 1, startIndex + Math.round(dy / rowH)))

    // Place the dragged row under the finger and open a gap for it. Distance is
    // measured in the list's own content, so scrolling moves the row on just as
    // much as the finger does.
    const applyDrag = () => {
        if (!dragging) return
        dy = pointerY - startY + (listEl.el.scrollTop - startScroll)
        dragging.move(dy)
        const target = targetIndex()
        getRows().forEach((r, i) => {
            if (r === dragging) return
            let off = 0
            if (startIndex < target && i > startIndex && i <= target) off = -rowH
            else if (startIndex > target && i < startIndex && i >= target) off = rowH
            r.shift(off)
        })
    }

    // Keep scrolling for as long as the finger rests near an edge — it stops
    // moving, so there are no more pointer events to drive this.
    const autoScroll = () => {
        if (!dragging) { frame = 0; return }
        const box = listEl.el.getBoundingClientRect()
        const speed = (gap: number) => MAX_SPEED * (1 - Math.max(0, gap) / EDGE)
        let step = 0
        if (pointerY - box.top < EDGE) step = -speed(pointerY - box.top)
        else if (box.bottom - pointerY < EDGE) step = speed(box.bottom - pointerY)
        if (step) {
            const before = listEl.el.scrollTop
            listEl.el.scrollTop = before + step
            // At the very top or bottom nothing moves, so nothing needs redrawing.
            if (listEl.el.scrollTop !== before) applyDrag()
        }
        frame = requestAnimationFrame(autoScroll)
    }

    listEl.el.addEventListener('pointerdown', (e: PointerEvent) => {
        if (e.button && e.button !== 0) return
        const r = findRow(e.target)
        if (!r) return
        startY = e.clientY
        dy = 0
        // The long-press only *arms* the row; the actual grab is deferred to the
        // first finger move (see pointermove). A press that never moves stays a
        // tap, so its click still reaches the item and toggles it — on Android a
        // deliberate tap often outlasts this timer. The lift is what tells the
        // user the row can be moved now, instead of leaving them holding on and
        // hoping (which is also when iOS used to pop its selection loupe).
        pressTimer = setTimeout(() => {
            armed = r
            r.lift()
            haptic()
        }, LIFT_DELAY)
    })

    // While dragging, stop the browser from turning finger movement into a list
    // scroll (which fires pointercancel and kills the drag). touch-action can't
    // do this — it's only read at gesture start, before the long-press fires.
    listEl.el.addEventListener('touchmove', (e: TouchEvent) => {
        if (dragging) e.preventDefault()
    }, { passive: false })

    listEl.el.addEventListener('pointermove', (e: PointerEvent) => {
        pointerY = e.clientY
        if (!dragging) {
            const delta = e.clientY - startY
            if (!armed) {
                // Moving before the long-press fires means the user is scrolling,
                // not grabbing — abandon the press so it stays a scroll.
                if (Math.abs(delta) > 8) clearTimeout(pressTimer)
                return
            }
            // Armed: commit to a drag only once the finger actually moves.
            if (Math.abs(delta) < 4) return
            dragging = armed
            startIndex = getRows().indexOf(dragging)
            startScroll = listEl.el.scrollTop
            rowH = dragging.el.offsetHeight || 56
            dragging.highlight()
            try { listEl.el.setPointerCapture(e.pointerId) } catch {}
            frame = requestAnimationFrame(autoScroll)
        }
        applyDrag()
    })

    // After a drag the browser may fire a click on release — swallow it so the
    // dropped item doesn't get toggled.
    const armClickGuard = () => {
        const guard = (ev: Event) => ev.stopPropagation()
        listEl.el.addEventListener('click', guard, { capture: true, once: true })
        setTimeout(() => listEl.el.removeEventListener('click', guard, true), 350)
    }

    const endDrag = () => {
        clearTimeout(pressTimer)
        if (armed && !dragging) armed.drop() // lifted but never moved — settle back
        armed = null
        if (!dragging) return
        if (frame) { cancelAnimationFrame(frame); frame = 0 }
        const moved = dragging
        dragging = null
        const target = targetIndex()
        const rows = getRows().slice()
        rows.splice(startIndex, 1)
        rows.splice(target, 0, moved)
        armClickGuard()

        // Where the finger left the row, before the DOM moves under it.
        const from = moved.el.getBoundingClientRect().top

        // Re-lay-out in the new order and clear the drag transforms in one batch,
        // reusing the existing rows (no rebuild). The rows that shifted aside are
        // already standing where the new order puts them, so dropping their
        // transform is invisible — only the dragged row has ground to cover.
        listEl._setChildren([])
        listEl.append(...rows)
        rows.forEach(r => r.style({ transform: 'translateY(0)', transition: 'none', zIndex: '' }))
        setRows(rows)
        onReorder(rows)

        // Then glide it home (FLIP): park it back under the finger, commit that
        // position, and release it — so the settle transition has somewhere to
        // travel from instead of both writes collapsing into one paint.
        const delta = from - moved.el.getBoundingClientRect().top
        moved.park(delta)
        void moved.el.offsetHeight
        moved.drop()
    }
    listEl.el.addEventListener('pointerup', endDrag)
    listEl.el.addEventListener('pointercancel', endDrag)

    return { isDragging: () => dragging !== null }
}
